import { Chip, Tooltip, CircularProgress } from "@mui/joy";
import { alertExamStatus } from "./utils/headExamStatus";
import useCountdown, { secondsToDHms } from "app/hooks/useCountdown";
import { TypeExamStatus } from "app/constants/types/exam.types";
import { getTextExamStatus } from "./utils/useExamStatus";

type Props = {
  type: TypeExamStatus;
  data?: {
    examDate?: string | Date;
    isEnrolled?: boolean;
  };
  loading?: boolean;
  error?: string | null;
};

const ExamStatus = ({ type, data, loading, error }: Props) => {
  // If there's an error, show error status
  if (error) {
    return (
      <Tooltip title="Error loading exam status">
        <Chip color="danger" variant="soft" sx={{ minWidth: "100px" }}>
          Error
        </Chip>
      </Tooltip>
    );
  }

  // Show loading state within the status chip
  const ChipContent = loading ? (
    <>
      {/* <CircularProgress size="sm" sx={{ mr: 1 }} /> */}
      <span>Loading...</span>
    </>
  ) : null;

  switch (type) {
    // New status: open (available for enrollment)
    case "open": {
      return (
        <Tooltip title={alertExamStatus.open}>
          <Chip
            color="primary"
            variant="solid"
            sx={{
              backgroundColor: "#FBB117",
              opacity: loading ? 0.7 : 1,
              minWidth: "100px",
              textAlign:"center",
            }}
          >
            {ChipContent || "Enroll Now"}
          </Chip>
        </Tooltip>
      );
    }

    // New status: close (enrollment closed)
    case "close": {
      return (
        <Tooltip title={alertExamStatus.close}>
          <Chip
            color="neutral"
            variant="soft"
            sx={{
              opacity: loading ? 0.7 : 1,
              minWidth: "100px",
              textAlign:"center",
            }}
          >
            {ChipContent || "Closed"}
          </Chip>
        </Tooltip>
      );
    }

    case "ended": {
      return (
        <Tooltip title="This exam has ended">
          <Chip
            color="neutral"
            variant="soft"
            sx={{
              opacity: loading ? 0.7 : 1,
              minWidth: "100px",
              textAlign:"center",
            }}
          >
            {ChipContent || "Ended"}
          </Chip>
        </Tooltip>
      );
    }

    case "not-enrolled": {
      return (
        <Tooltip title="Click to enrolled on this exam">
          <Chip
            color="neutral"
            variant="solid"
            sx={{
              opacity: loading ? 0.7 : 1,
              minWidth: "100px",
              textAlign:"center",
            }}
          >
            {ChipContent || "Not Enrolled"}
          </Chip>
        </Tooltip>
      );
    }
    case "submitted": {
      return (
        <Tooltip title="This exam has ended">
          <Chip
            color="warning"
            variant="soft"
            sx={{
              opacity: loading ? 0.7 : 1,
              minWidth: "100px",
              textAlign:"center",
            }}
          >
            {ChipContent || "Submitted"}
          </Chip>
        </Tooltip>
      );
    }
    case "graded": {
      return (
        <Tooltip title="Exam has been graded">
          <Chip
            color="success"
            variant="solid"
            sx={{
              backgroundColor: "#29B14E",
              opacity: loading ? 0.7 : 1,
              minWidth: "100px",
              textAlign:"center",
            }}
          >
            {ChipContent || "Graded"}
          </Chip>
        </Tooltip>
      );
    }
    case "ongoing": {
      return (
        <Tooltip title="Exam is in progress">
          <Chip
            color="primary"
            variant="solid"
            sx={{
              opacity: loading ? 0.7 : 1,
              minWidth: "100px",
              textAlign:"center",
            }}
          >
            {ChipContent  || "On Going"}
          </Chip>
        </Tooltip>
      );
    }
    case "missing": {
      return (
        <Tooltip title={alertExamStatus.missing}>
          <Chip
            color="danger"
            variant="solid"
            sx={{
              opacity: loading ? 0.7 : 1,
              minWidth: "100px",
              textAlign:"center",
            }}
          >
            {ChipContent || "Pending Submission"}
          </Chip>
        </Tooltip>
      );
    }
    case "pending": {
      return (
        <Tooltip title="Your submission is being graded">
          <Chip
            color="danger"
            variant="solid"
            sx={{
              backgroundColor: "#EA7D7D",
              opacity: loading ? 0.7 : 1,
              minWidth: "100px",
              textAlign:"center",
            }}
          >
            {ChipContent || "Pending"}
          </Chip>
        </Tooltip>
      );
    }
    // New status: standby (enrolled user, locked exam)
    case "standby": {
      return (
        <Tooltip title={alertExamStatus.standby}>
          <Chip
            color="warning"
            variant="solid"
            sx={{
              backgroundColor: "#F4B837",
              color: "neutral.solidActiveBg",
              opacity: loading ? 0.7 : 1,
              minWidth: "100px",
              textAlign:"center",
            }}
          >
            {ChipContent || "Stand By"}
          </Chip>
        </Tooltip>
      );
    }

    // New status: countdown (enrolled user, exam about to start)
    case "countdown": {
      const timeLeft = data?.examDate
        ? useCountdown(new Date(data?.examDate) || new Date())
        : 0;
      const thresholdTime = 30 * 60 * 1000;
      const isCountdownActive =
        timeLeft &&
        data?.examDate &&
        new Date().getTime() >=
          new Date(data.examDate).getTime() - thresholdTime;

      // Dynamic tooltip that combines the static message with countdown information
      const countdownTooltip = `${alertExamStatus.countdown} (${secondsToDHms(
        timeLeft
      )} remaining)`;

      return (
        <Tooltip title={countdownTooltip}>
          <Chip
            color="warning"
            variant="solid"
            sx={{
              backgroundColor: "#ff9a00",
              color: "neutral.solidActiveBg",
              maxWidth: "170px",
              opacity: loading ? 0.7 : 1,
              minWidth: "100px",
            }}
          >
            {ChipContent ||
              (isCountdownActive ? (
                <>
                  <span>{"🔥 Start in: "}</span>
                  <b>{secondsToDHms(timeLeft, true)}</b>
                </>
              ) : (
                <span>Starting Soon</span>
              ))}
          </Chip>
        </Tooltip>
      );
    }

    // For backward compatibility
    case "standby-open":
    case "standby-locked": {
      const timeLeft = data?.examDate
        ? useCountdown(new Date(data?.examDate) || new Date())
        : 0;
      const isLocked = type == "standby-locked";
      const thresholdTime = 30 * 60 * 1000;
      const isCountdownActive =
        !isLocked &&
        timeLeft &&
        data?.examDate &&
        new Date().getTime() >=
          new Date(data.examDate).getTime() - thresholdTime;

      return (
        <Tooltip
          title={
            isCountdownActive
              ? "Exam will start in " +
                secondsToDHms(timeLeft) +
                ". Don't forget to download your exam paper."
              : isLocked
              ? "Upcoming Exam"
              : "Your Exam is Open"
          }
        >
          <Chip
            color="warning"
            variant="solid"
            sx={{
              backgroundColor: isLocked ? "#F4B837" : "#ff9a00",
              color: "neutral.solidActiveBg",
              maxWidth: "170px",
              opacity: loading ? 0.7 : 1,
              minWidth: "100px",
            }}
          >
            {ChipContent ||
              (isLocked ? (
                <span>Upcoming</span>
              ) : isCountdownActive ? (
                <>
                  <span>{"🔥 Start in: "}</span>
                  <b>{secondsToDHms(timeLeft, true)}</b>
                </>
              ) : (
                <span>Open</span>
              ))}
          </Chip>
        </Tooltip>
      );
    }
    default: {
      return <></>;
    }
  }
};

export default ExamStatus;
